import { create } from 'zustand';
import { MemberInfoData } from '../dto/EntityRepository';
import type { ResourceScope, ResourceStatus } from '../util/scopedResource';

interface MemberInfoState {
  memberInfo: MemberInfoData | null;
  memberInfoOwnerAui: string | null;
  memberInfoScope: ResourceScope | null;
  status: ResourceStatus;
  hasChanged: boolean;
  imageChanged: boolean;
  beginMemberInfoFetch: (ownerAui: string, scope: ResourceScope) => void;
  setMemberInfo: (memberInfo: MemberInfoData, ownerAui: string, scope: ResourceScope) => void;
  setMemberInfoError: () => void;
  updatMemberInfo: (updates: Partial<MemberInfoData>) => void;
  updateImage: (originUrl: string, width: number, height: number) => void;
  clearMemberInfo: () => void;
  setMemberInfoEngVisibleAfterUpdate: (updatedMemberInfo: MemberInfoData) => void;
}

const isSameMemberInfo = (prev: MemberInfoData | null, next: MemberInfoData): boolean => {
  if (!prev) return false;
  return (
    prev.id === next.id &&
    prev.name === next.name &&
    prev.nameEn === next.nameEn &&
    prev.year === next.year &&
    prev.country === next.country &&
    prev.email === next.email &&
    prev.contact === next.contact &&
    prev.description === next.description &&
    prev.descriptionEn === next.descriptionEn &&
    prev.engVisible === next.engVisible &&
    prev.uploadFile.id === next.uploadFile.id &&
    prev.uploadFile.originUrl === next.uploadFile.originUrl &&
    prev.uploadFile.width === next.uploadFile.width &&
    prev.uploadFile.height === next.uploadFile.height &&
    prev.uploadFile.allowDownload === next.uploadFile.allowDownload
  );
};

export const useMemberInfoStore = create<MemberInfoState>((set) => ({
  memberInfo: null,
  memberInfoOwnerAui: null,
  memberInfoScope: null,
  status: 'idle',
  hasChanged: false,
  imageChanged: false,
  beginMemberInfoFetch: (ownerAui, scope) => set((state) => {
    // 같은 owner+scope 재fetch: success 유지(SWR). 다르면 loading으로 숨김.
    if (
      state.memberInfoOwnerAui === ownerAui &&
      state.memberInfoScope === scope &&
      state.status === 'success'
    ) {
      return state;
    }
    return { status: 'loading' };
  }),
  setMemberInfo: (memberInfo, ownerAui, scope) =>
    set((state) => {
      if (
        state.status === 'success' &&
        state.memberInfoOwnerAui === ownerAui &&
        state.memberInfoScope === scope &&
        isSameMemberInfo(state.memberInfo, memberInfo) &&
        !state.hasChanged &&
        !state.imageChanged
      ) {
        return state;
      }
      return {
        memberInfo: { ...memberInfo },
        memberInfoOwnerAui: ownerAui,
        memberInfoScope: scope,
        status: 'success',
        hasChanged: false,
        imageChanged: false,
      };
    }),
  setMemberInfoError: () => set({ status: 'error' }),
  updatMemberInfo: (updates) =>
    set(({ memberInfo }) => ({
      memberInfo: memberInfo ?
        { ...memberInfo, ...updates }
        : null,
      hasChanged: true
    })),
  updateImage: (originUrl: string, width: number, height: number) =>
    set(({ memberInfo }) => ({
      memberInfo: memberInfo ? {
        ...memberInfo,
        uploadFile: {
          ...memberInfo.uploadFile,
          originUrl,
          width,
          height,
        }
      } : null,
      hasChanged: true,
      imageChanged: true,
    })),
  clearMemberInfo: () => set((state) => {
    if (
      state.memberInfo === null &&
      state.memberInfoOwnerAui === null &&
      state.memberInfoScope === null &&
      state.status === 'idle' &&
      state.hasChanged === false &&
      state.imageChanged === false
    ) {
      return state;
    }
    return {
      memberInfo: null,
      memberInfoOwnerAui: null,
      memberInfoScope: null,
      status: 'idle',
      hasChanged: false,
      imageChanged: false,
    };
  }),
  setMemberInfoEngVisibleAfterUpdate: (updatedMemberInfo) =>
    set((state) => ({
      memberInfo: state.memberInfo
        ? {
            ...state.memberInfo,
            engVisible:
              updatedMemberInfo.engVisible !== undefined
                ? updatedMemberInfo.engVisible
                : state.memberInfo.engVisible,
          }
        : null,
      hasChanged: state.hasChanged ?? false,
      imageChanged: state.imageChanged ?? false,
    })),
}));
